import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import validarCPF from '../util/validarCpf';

const validateEmail = emailValue => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(emailValue);
};

export default function RecuperarSenhaModal({
  showResetPasswordModal,
  setShowResetPasswordModal,
  userType,
}) {
  let validationHint = '';
  if (userType === 'externo') {
    validationHint = '(*consulte a administração do ICM*)';
  } else if (userType === 'paciente') {
    validationHint = '(*código disponível no laudo*)';
  }

  const [email, setEmail] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [code, setCode] = useState('');
  const [validacao, setValidacao] = useState('');
  const [cpf, setCPF] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSendEmail = async () => {
    if (!email || !cpf)
      return toast.warn('Dados obrigatórios não preenchidos!');

    if (!validateEmail(email)) {
      return toast.warn('Email inválido!');
    }

    if (!validarCPF(cpf)) {
      return toast.warn('CPF inválido!');
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${userType}/send/forgot-password-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, cpf }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        toast.error(data.message || data.error || 'Erro ao enviar email.');
        return;
      }

      setIsEmailSent(true);
      toast.success(data.message || 'Email enviado.');
    } catch (error) {
      toast.error(error.message || 'Erro ao enviar email.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!cpf) return toast.warn('CPF é obrigatório!');
    if (!code) return toast.warn('Código de verificação é obrigatório!');
    if (!validacao) return toast.warn('Código de validação é obrigatório!');
    if (!newPassword) return toast.warn('Senha é obrigatória!');
    if (!confirmNewPassword)
      return toast.warn('Confirmação de senha é obrigatória!');

    if (!validarCPF(cpf)) {
      return toast.warn('CPF inválido!');
    }

    if (newPassword.length < 6) {
      return toast.warn('A senha deve ter no mínimo 6 caracteres!');
    }

    if (newPassword !== confirmNewPassword) {
      return toast.warn('As senhas não coincidem!');
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${userType}/reset-password`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cpf, code, validacao, newPassword }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        toast.error(data.message || data.error || 'Erro ao atualizar senha.');
        return;
      }

      toast.success(data.message || 'Senha atualizada.');
      handleClose();
    } catch (error) {
      toast.error(error.message || 'Erro ao atualizar senha.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setCPF('');
    setCode('');
    setValidacao('');
    setNewPassword('');
    setConfirmNewPassword('');
    setIsEmailSent(false);
    setShowResetPasswordModal(false);
  };

  return (
    <Modal show={showResetPasswordModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Registrar senha</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isEmailSent ? (
          <>
            <Form.Group controlId="resetPasswordEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                autoComplete="email"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Seu email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="resetPasswordCPF">
              <Form.Label>CPF</Form.Label>
              <Form.Control
                type="text"
                name="cpf"
                inputMode="numeric"
                autoComplete="username"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Seu CPF"
                value={cpf}
                onChange={e => setCPF(e.target.value)}
              />
            </Form.Group>
          </>
        ) : (
          <>
            <Form.Group controlId="resetPasswordCPF">
              <Form.Label>CPF</Form.Label>
              <Form.Control
                type="text"
                name="cpf"
                inputMode="numeric"
                autoComplete="username"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Seu CPF"
                value={cpf}
                onChange={e => setCPF(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="resetPasswordVerificationCode">
              <Form.Label>Código de verificação</Form.Label>
              <Form.Control
                type="text"
                name="verification-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Código recebido por email"
                value={code}
                onChange={e => setCode(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="resetPasswordValidationCode">
              <Form.Label>Código de validação {validationHint}</Form.Label>
              <Form.Control
                type="text"
                name="validation-code"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Consulte a administração do ICM"
                value={validacao}
                onChange={e => setValidacao(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="resetPasswordNewPassword">
              <Form.Label>Nova senha</Form.Label>
              <Form.Control
                type="password"
                name="new-password"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Sua nova senha"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="resetPasswordConfirmPassword">
              <Form.Label>Confirmar senha</Form.Label>
              <Form.Control
                type="password"
                name="confirm-new-password"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Confirme sua senha"
                value={confirmNewPassword}
                onChange={e => setConfirmNewPassword(e.target.value)}
              />
            </Form.Group>
          </>
        )}

        {isEmailSent && (
          <Alert variant="info">
            <strong>
              Você receberá um email com um código de verificação para recuperar
              sua senha.
            </strong>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Fechar
        </Button>
        <Button
          onClick={isEmailSent ? handleVerifyCode : handleSendEmail}
          disabled={isSubmitting}
        >
          {isEmailSent ? 'Mudar senha' : 'Enviar código'}
          {isSubmitting && (
            <Spinner animation="border" size="sm" className="me-2 ml-2" />
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
